import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useRefreshA2pStatusMutation, useCancelA2pRegistrationMutation } from "@/store/rest_api";
import A2pRegistration from "@/model/interface/A2pRegistration";
import { formatDate } from "@/util/date";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle, Clock, XCircle } from "lucide-react";

interface Props {
  registration: A2pRegistration;
}

function StatusIcon({ status }: { status?: string }) {
  if (!status) return <Clock className="h-5 w-5 text-yellow-600" />;
  const lower = status.toLowerCase();
  if (lower.includes("approved") || lower.includes("verified") || lower === "completed")
    return <CheckCircle className="h-5 w-5 text-green-600" />;
  if (lower.includes("failed") || lower.includes("rejected"))
    return <XCircle className="h-5 w-5 text-red-600" />;
  return <Clock className="h-5 w-5 text-yellow-600" />;
}

function TimelineRow({ label, submittedAt, approvedAt }: { label: string; submittedAt?: string; approvedAt?: string }) {
  if (!submittedAt && !approvedAt) return null;
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="font-medium">{label}</span>
      <span className="text-muted-foreground">
        {submittedAt && `Submitted ${formatDate(submittedAt)}`}
        {submittedAt && approvedAt && " | "}
        {approvedAt && `Approved ${formatDate(approvedAt)}`}
      </span>
    </div>
  );
}

function StatusRow({ label, sid, status }: { label: string; sid?: string; status?: string }) {
  if (!sid) return null;
  return (
    <div className="flex items-center justify-between border rounded-lg p-3">
      <div className="flex items-center gap-3">
        <StatusIcon status={status} />
        <div>
          <div className="font-medium text-sm">{label}</div>
          <code className="text-xs text-muted-foreground">{sid}</code>
        </div>
      </div>
      <Badge variant={status?.toLowerCase().includes("approved") || status?.toLowerCase().includes("verified") ? "default" : "secondary"}>
        {status || "Pending"}
      </Badge>
    </div>
  );
}

export default function RegistrationStatusStep({ registration }: Props) {
  const [refreshStatus, { isLoading: isRefreshing }] = useRefreshA2pStatusMutation();
  const [cancelRegistration, { isLoading: isCancelling }] = useCancelA2pRegistrationMutation();

  const handleRefresh = async () => {
    try {
      await refreshStatus().unwrap();
      toast.success("Status refreshed");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to refresh status");
    }
  };

  const handleCancel = async () => {
    try {
      await cancelRegistration().unwrap();
      toast.success("A2P registration cancelled");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to cancel registration");
    }
  };

  const isComplete = registration.status === "COMPLETED";
  const canCancel = !!registration.campaignSid && registration.status !== "CANCELLED";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Registration Status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isComplete && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              Your A2P 10DLC registration is complete. You can now send SMS messages from your registered phone numbers.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm font-semibold">Overall Status:</span>
          <Badge variant={isComplete ? "default" : "secondary"} className="text-sm">
            {registration.status.replace(/_/g, " ")}
          </Badge>
          {registration.brandScore != null && (
            <Badge variant="outline">Brand Score: {registration.brandScore}</Badge>
          )}
        </div>

        <div className="space-y-3">
          <StatusRow label="Customer Profile" sid={registration.customerProfileSid} status={registration.customerProfileStatus} />
          <StatusRow label="Trust Product" sid={registration.trustProductSid} status={registration.trustProductStatus} />
          <StatusRow label="Brand Registration" sid={registration.brandRegistrationSid} status={registration.brandStatus} />
          <StatusRow label="Messaging Service" sid={registration.messagingServiceSid} status="Active" />
          <StatusRow label="Campaign" sid={registration.campaignSid} status={registration.campaignStatus} />
        </div>

        {(registration.profileSubmittedAt || registration.brandSubmittedAt || registration.campaignSubmittedAt) && (
          <div className="rounded-lg border p-3 space-y-2">
            <div className="text-sm font-semibold">Timeline</div>
            <TimelineRow
              label="Business Profile"
              submittedAt={registration.profileSubmittedAt}
              approvedAt={registration.profileApprovedAt}
            />
            <TimelineRow
              label="Brand"
              submittedAt={registration.brandSubmittedAt}
              approvedAt={registration.brandApprovedAt}
            />
            <TimelineRow
              label="Campaign"
              submittedAt={registration.campaignSubmittedAt}
              approvedAt={registration.campaignApprovedAt}
            />
          </div>
        )}

        {registration.rejectionReason && (
          <Alert variant="destructive">
            <AlertDescription>{registration.rejectionReason}</AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        {canCancel ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={isCancelling}>
                {isCancelling && <Loader2 className="animate-spin mr-1 h-4 w-4" />}
                Cancel Registration
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel A2P 10DLC registration?</AlertDialogTitle>
                <AlertDialogDescription>
                  This stops the monthly campaign fee, deletes your A2P campaign at the carrier, and un-enrolls your
                  phone numbers, so SMS sending will be blocked until you register again. Your approved brand is kept.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep registration</AlertDialogCancel>
                <AlertDialogAction onClick={handleCancel}>Cancel registration</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <span />
        )}
        <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
          {isRefreshing ? <Loader2 className="animate-spin mr-1 h-4 w-4" /> : <RefreshCw className="mr-1 h-4 w-4" />}
          Refresh Status
        </Button>
      </CardFooter>
    </Card>
  );
}
